import { logError } from '@release/shared/lib/logger';
import { FC, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useFeatureFlagsReady } from '@release/feature-flag';
import { useRouter } from 'next/router';
import clsx from 'clsx';

import { ENTRY_FALLBACK_PATH, resolveEntryPath } from '../utils/resolveEntryPath';

import { BaseLayout, BaseLayoutProps } from '@/components/BaseLayout';
import { ChatSkeletonContainer } from '@/features/chat/common/List/ChatSkeletonContainer';
import { ANKEN_APP_PATH, HOME_PATH } from '@/constants';
import { useRealtorSidebarItems } from '@/hooks/useRealtorSidebarItems';

interface LayoutProps extends Pick<
  BaseLayoutProps,
  'align' | 'skeleton' | 'maxWidth' | 'children' | 'className' | 'hideBottomTab'
> {}

// 管理画面の共通レイアウト(Sidebar/BottomTab + Main)
export const Layout: FC<LayoutProps> = ({ className, skeleton, children, ...props }) => {
  const router = useRouter();
  const {
    sidebarItems,
    bottomTabItems,
    chatUnreadCountValue,
    hasChatMembership,
    useZaiko,
    showHanbaiTab,
    showBaikaiTab,
    identificationsFeatureFlag,
    orgInfo,
    me,
    chatUnreadReady,
    organizationIsError,
  } = useRealtorSidebarItems();

  // 入口解決: 契約一覧（ANKEN_APP_PATH）を直接開いたが契約を使えないユーザーを、
  // ロール・契約に応じた最初の使える画面へ振り分ける（resolveEntryPath）。
  // ホーム（/）では振り分けない。判定材料が揃う前に確定させない。
  const flagsReady = useFeatureFlagsReady();
  const organizationUnavailable = orgInfo === undefined && organizationIsError;
  const isEntryReady =
    (orgInfo !== undefined || organizationUnavailable) && Boolean(me?.info) && chatUnreadReady && flagsReady;
  const entryPath = useMemo(() => {
    if (organizationUnavailable) {
      return ENTRY_FALLBACK_PATH;
    }
    if (!orgInfo || !me?.info) {
      return undefined;
    }
    return resolveEntryPath({
      enableAnken: orgInfo.enableAnken,
      enableChat: orgInfo.enableChat,
      enableKyc: orgInfo.enableKyc,
      enableMypageAdmin: orgInfo.enableMypageAdmin,
      roles: me.info.roles,
      hasChatMembership,
      useZaiko: Boolean(useZaiko),
      showHanbai: Boolean(showHanbaiTab),
      showBaikai: Boolean(showBaikaiTab),
      identificationsFlag: identificationsFeatureFlag,
    });
  }, [
    organizationUnavailable,
    orgInfo,
    me?.info,
    hasChatMembership,
    useZaiko,
    showHanbaiTab,
    showBaikaiTab,
    identificationsFeatureFlag,
  ]);
  const entryRedirectedRef = useRef(false);
  // 契約一覧では、留まると分かるまで（orgInfo/me 未ロード or 別画面へ振り分け予定）子を描画しない。
  // 契約一覧の Content は mount 直後に案件 API を叩くため、権限のないユーザーが転送前に PermissionDenied を踏むのを防ぐ
  const holdEntry = router.pathname === ANKEN_APP_PATH && (entryPath === undefined || entryPath !== ANKEN_APP_PATH);

  useLayoutEffect(() => {
    if (!router.isReady) {
      return;
    }
    if (router.pathname !== ANKEN_APP_PATH) {
      entryRedirectedRef.current = false;
    }

    // NOTE: 販売管理が無効の場合、ホームへ戻す
    if (useZaiko !== undefined && !useZaiko && router.pathname.startsWith('/anken/zaiko')) {
      void router.replace(HOME_PATH);
      return;
    }

    if (
      router.pathname === ANKEN_APP_PATH &&
      isEntryReady &&
      entryPath !== undefined &&
      entryPath !== ANKEN_APP_PATH &&
      !entryRedirectedRef.current
    ) {
      entryRedirectedRef.current = true;
      void router.replace(entryPath);
    }
  }, [useZaiko, isEntryReady, entryPath, router]);

  useEffect(() => {
    // バックグラウンドでのバッジを付与するために、serviceWorkerのキャッシュに未読数を保存
    if ('caches' in window) {
      window.caches
        .open('chat')
        .then((cache) => {
          const customResponse = new Response(chatUnreadCountValue ? chatUnreadCountValue.toString() : '0', {
            headers: { 'Content-Type': 'text/plain' },
          });
          cache.put('/unread-count', customResponse).catch(logError);
          if ('setAppBadge' in navigator) {
            navigator.setAppBadge(chatUnreadCountValue ?? 0).catch(logError);
          }
        })
        .catch(logError);
    }
  }, [chatUnreadCountValue]);

  return (
    <BaseLayout
      sidebarItems={sidebarItems}
      bottomTabItems={bottomTabItems}
      className={clsx('bg-sem-l-bg-primary', className)}
      skeleton={skeleton ?? <ChatSkeletonContainer />}
      {...props}
    >
      {holdEntry ? (skeleton ?? <ChatSkeletonContainer />) : children}
    </BaseLayout>
  );
};
